/**
 * Pure knowledge-base helpers. No DOM, so the grouping and freshness
 * rules can be tested without a browser.
 */

import { formatRelative } from '../../core/format.js';

export const DEPTH_ORDER = ['analytics', 'code', 'revenue', 'ads'];

export const DEPTH_LABELS = {
  analytics: 'Behaviour and funnels',
  code: 'Code and releases',
  revenue: 'Revenue and stores',
  ads: 'Ads and channel',
};

export const BLIND_COPY = {
  analytics: 'Connect an analytics source so answers include how people use the product.',
  code: 'Connect a code source so answers include what shipped.',
  revenue: 'Connect a revenue source so answers include who pays.',
  ads: 'Connect an ad source so answers include where users come from.',
};

/** @param {string} [type] */
export function memoryGroup(type) {
  if (type === 'instruction') return 'instruction';
  if (type === 'decision') return 'decision';
  if (type === 'todo') return 'todo';
  return 'fact';
}

/** @param {object[]} [memories] */
export function groupMemories(memories) {
  const grouped = { instruction: [], fact: [], decision: [], todo: [] };
  for (const memory of memories || []) {
    grouped[memoryGroup(memory && memory.memory_type)].push(memory);
  }
  return grouped;
}

function countLabel(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

/** @param {{ fact: number, decision: number, todo: number }} counts */
export function memorySplit(counts) {
  return [
    countLabel(counts.fact, 'fact', 'facts'),
    countLabel(counts.decision, 'decision', 'decisions'),
    countLabel(counts.todo, 'to-do', 'to-dos'),
  ].join(' · ');
}

/** @param {object[]} [memories] @returns {string | null} */
export function newestAt(memories) {
  let best = null;
  for (const memory of memories || []) {
    const t = memory && memory.created_at ? new Date(memory.created_at).getTime() : NaN;
    if (!Number.isFinite(t)) continue;
    if (best == null || t > best) best = t;
  }
  return best == null ? null : new Date(best).toISOString();
}

/** @param {unknown} n */
export function formatBytes(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return '—';
  if (x < 1024) return `${Math.round(x)} B`;
  if (x < 1024 * 1024) return `${Math.round(x / 1024)} KB`;
  return `${(x / (1024 * 1024)).toFixed(1)} MB`;
}

/** @param {object} [doc] */
export function documentMeta(doc) {
  const raw = doc && doc.metadata;
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(String(raw)); } catch { return {}; }
}

/** @param {object} [doc] */
export function writtenByBonnie(doc) {
  return documentOrigin(doc) === 'bonnie';
}

/** @param {object} [doc] @returns {'bonnie'|'connector'|'upload'} */
export function documentOrigin(doc) {
  const src = String((doc && doc.source) || '');
  const meta = documentMeta(doc);
  if (src === 'agent' || meta.schedule_id || meta.generated_by || meta.origin === 'schedule') {
    return 'bonnie';
  }
  if (src === 'import' || src === 'api' || meta.origin === 'github_sync') return 'connector';
  return 'upload';
}

/** Stable short code from the real id, not a sample sequence. */
export function shortCode(id, prefix) {
  const tail = String(id || '').replace(/-/g, '').slice(-4).toUpperCase();
  return tail ? `${prefix}-${tail}` : prefix;
}

/**
 * Mode and last sync when the connector list has them (R6).
 * Otherwise every source is live, checked at last_tested_at.
 * @param {object} [connector]
 * @param {number} [now]
 */
export function sourceFreshness(connector, now = Date.now()) {
  const mode = connector && (connector.mode === 'synced' || connector.mode === 'live')
    ? connector.mode
    : '';
  if (mode && connector.last_synced_at) {
    return `${mode} · ${formatRelative(connector.last_synced_at, now)}`;
  }
  if (connector && connector.last_tested_at) {
    return `live · checked ${formatRelative(connector.last_tested_at, now)}`;
  }
  return 'live · not checked';
}

/** @param {object} [connector] */
export function sourceName(connector) {
  if (!connector) return 'Source';
  return connector.provider_display || connector.label || connector.provider_name || 'Source';
}

/** @param {Record<string, number>} [breakdown] */
export function answerable(breakdown) {
  const values = breakdown || {};
  return DEPTH_ORDER.filter((key) => Number(values[key]) > 0);
}

/** @param {Record<string, number>} [breakdown] */
export function blindSpots(breakdown) {
  const values = breakdown || {};
  return DEPTH_ORDER.filter((key) => !(Number(values[key]) > 0));
}

const SOURCE_LABELS = {
  chat: 'Chat',
  document: 'Document',
  manual: 'Manual',
  analysis: 'Analysis',
};

/** @param {object} [memory] */
export function sourceBadge(memory) {
  const key = String((memory && memory.source) || '').toLowerCase();
  return SOURCE_LABELS[key] || null;
}

/**
 * Status after the source badge: expires / Pinned / Permanent (AI).
 * @param {object} [memory]
 * @param {number} [now]
 * @returns {{ label: string, title?: string, kind: string }|null}
 */
export function memoryLifeBadge(memory, now = Date.now()) {
  if (!memory) return null;
  if (memory.pinned) return { label: 'Pinned', kind: 'pinned' };
  if (memory.permanence === 'permanent') {
    return {
      label: 'Permanent (AI)',
      kind: 'permanent',
      title: memory.permanence_reason ? String(memory.permanence_reason) : 'Kept for the product lifetime',
    };
  }
  if (memory.expires_at) {
    const ms = Date.parse(memory.expires_at) - now;
    if (!Number.isFinite(ms)) return null;
    if (ms <= 0) return { label: 'Expired', kind: 'expired' };
    const days = Math.max(1, Math.ceil(ms / (24 * 60 * 60 * 1000)));
    return { label: `expires in ${days} day${days === 1 ? '' : 's'}`, kind: 'expires' };
  }
  return null;
}
